import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getMachineTypeCategory } from "../utils/sapMetadataTranslations";

interface MachineInfoProps {
  machineId?: string;
  machineName?: string;
  lineId?: string;
  lineName?: string;
  lineNumber?: string;
  machineType?: string;
  factory?: string;
}

const DEFAULT_MACHINE_INFO = {
  line: "Production Line Alpha",
  name: "Packer",
  id: "56881",
  factory: "1201 (Factory Name)",
};

const MachineInfo = ({
  machineId = DEFAULT_MACHINE_INFO.id,
  machineName = DEFAULT_MACHINE_INFO.name,
  lineId = DEFAULT_MACHINE_INFO.line,
  lineName,
  lineNumber,
  machineType,
  factory = DEFAULT_MACHINE_INFO.factory,
}: MachineInfoProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get the machine type category for translation
  const typeCategory = getMachineTypeCategory(machineType || "");
  const translatedType = t(
    `machineTypeCategories.${typeCategory}`,
    typeCategory,
  );

  // machineName arrives pre-formatted as "<id> - <model> (<id>)"; strip the
  // redundant id so we can show just the model (e.g. "Modell 350") next to the
  // type. The big title becomes the machine id on its own.
  const escId = String(machineId).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const model = (machineName || "")
    .replace(new RegExp(`^\\s*${escId}\\s*-\\s*`), "")
    .replace(new RegExp(`\\s*\\(${escId}\\)\\s*$`), "")
    .trim();
  const typeDisplay = [model, translatedType].filter(Boolean).join(" ");

  // Format line display: show lineNumber with lineName if both available
  const lineDisplay =
    lineNumber && lineName
      ? `${lineNumber} - ${lineName}`
      : lineNumber || lineName || lineId;

  const handleLineClick = () => {
    if (lineId) {
      navigate(`/line/${lineId}`, {
        state: { lineName, lineNumber, factory },
      });
    }
  };

  return (
    <div className="flex-1 text-on-header">
      <div className="text-lg md:text-2xl font-extrabold tracking-wide mb-1 md:mb-2">
        {machineId}
      </div>
      <div className="space-y-0.5 md:space-y-1 text-sm md:text-lg font-mono">
        <div>
          <span className="font-semibold text-on-header">{t("type")}:</span>{" "}
          {typeDisplay}
        </div>
        <div>
          <span className="font-semibold text-on-header">Line:</span>{" "}
          <span
            onClick={handleLineClick}
            className="cursor-pointer hover:text-on-header hover:underline transition-colors"
          >
            {lineDisplay}
          </span>
        </div>
        <div>
          <span className="font-semibold text-on-header">Factory:</span> {factory}
        </div>
      </div>
    </div>
  );
};

export default MachineInfo;
